<script lang="ts" setup>
import {
  ref,
  computed,
  onMounted,
  onUnmounted,
  ComponentPublicInstance,
  watch,
  reactive
} from 'vue'
import { usePayrollStore } from '@/store/payroll-data'
import { useRouter } from 'vue-router'
import { useField, useForm } from 'vee-validate'
import { object, array, date, string } from 'yup'
import { useNotificationsStore } from '@/store/notifications'
import { usePayrollChange } from '@/app/core/composable/routes/useChangePayroll'
import { getErrorCode } from '@/app/core/composable/general'
import PaginatedTable from '@/app/components/PaginatedTable.vue'
import { useI18n } from 'vue-i18n'
import dayjs from 'dayjs'
import * as XLSX from 'xlsx'
import {
  DCard,
  DBtn,
  DTextField,
  DSelectFieldSearch,
  DTagsField,
  DTable,
  DModal,
  DEmployeesFilter,
  DLoading,
  FilterLinesIcon,
  Users01Icon,
  UploadCloud01Icon,
  ChevronDownIcon,
  DMultiselect,
  DFileUploader,
  DToggleField,
  DIcon
} from 'front-library'
// } from '../../../../../../../front-library/src/components/index.ts'
import {
  StructureRowsRetimbrado,
  ReportGoSocket4,
  convertArrayToString,
  getProcessCountry,
  getClosedPayrolls,
  getReportRequest,
  generateGoSocket4,
  getEmployees,
  getPayroll,
  exportExcel,
  getStampedPayrolls,
  stampedPayrolls
} from '@/app/core/services/reports'
import { useUserStore } from '@/store/user'

usePayrollChange(() => window.location.reload())

/* store, router, notifications */
const storePayroll = usePayrollStore()
const store = useUserStore()
const router = useRouter()
const notifications = useNotificationsStore()
const { t } = useI18n()

/* Propiedades Computadas */
const filterOptions = computed(() => {
  return (items: any, search: any) =>
    (items = items.filter(
      (item: any) =>
        item.name.toLowerCase().indexOf(search.toString().toLowerCase()) > -1
    ))
})

/* Datos table */
const columns = ref([
  { key: 'codigo_name', title: t('reports.columns.employee'), type: 'text' },
  { key: 'relationId', title: t('reports.columns.relation'), type: 'text' },
  { key: 'uuid', title: t('reports.columns.uuid'), type: 'array' }
])

/* Datos table */
const state = reactive<{ rows: StructureRowsRetimbrado[] }>({
  rows: []
})
const filterItems = ref<string[]>([])
const perPage = ref(0)
const offset = ref(0)

const collapseTable = ref<HTMLElement | null>(null)
const iconChevron = ref<HTMLElement | null>(null)
const showBtn = ref<HTMLElement | null>(null)
const tableRetimbrado = ref<ComponentPublicInstance | null>(null)
const fileInput = ref<HTMLInputElement | null>(null)

/* Datos componente Modal y Loading */
const showModal = ref(false)
const titleModal = ref('')
const descriptionModal = ref('')
const typeAlertModal = ref('')
const acceptQuestion = ref()

const loading = ref(false)
const messageLoading = ref('')

/* Datos Formulario */
const refresh = ref()
const urlFile = ref(null)
const currencyFrom = ref('')
const itemsProcesses = ref<string[]>([])
const itemsPayrolls = ref<any[]>([])
const searchPayroll = ref('')
const searchProcess = ref('')
const itemsClosedPayroll = ref<string[]>([])
const searchClosedPayroll = ref('')
const itemsEmployees = ref<string[]>([])
const createGoSocket4 = ref<ReportGoSocket4>()

const { value: isStampedPayroll } = useField<boolean>(ref('isStampedPayroll'))
const { value: wantStampedPayroll } = useField<boolean>(ref('wantStampedPayroll'))

const itemsRegymeType = ref([
  { id: '02', name: '02-Sueldos' },
  { id: '03', name: '03-Jubilados' },
  { id: '04', name: '04-Pensionados' },
  { id: '09', name: '09-Asimilados Honorarios' },
  { id: '13', name: '13-Indemnización o Separación' }
])
const searchRegymeType = ref<string>('')

const itemsPaymentFrequency = ref([
  { id: '01', name: '01-Diario' },
  { id: '02', name: '02-Semanal' },
  { id: '03', name: '03-Catorcenal' },
  { id: '04', name: '04-Quincenal' },
  { id: '05', name: '05-Mensual' },
  { id: '06', name: '06-Bimestral' },
  { id: '07', name: '07-Unidad_obra' },
  { id: '08', name: '08-Comisión' },
  { id: '09', name: '09-Precio_alzado' },
  { id: '10', name: '10-Decenal' },
  { id: '99', name: '99-Otra Periodicidad' }
])
const searchPaymentFrequency = ref<string>('')

const itemsRelationDocuments = ref([
  { id: '01', name: '01-Nota de crédito de los documentos relacionados.' },
  { id: '02', name: '02-Nota de débito de los documentos relacionados.' },
  {
    id: '03',
    name: '03-Devolución de mercancía sobre facturas o traslados previos.'
  },
  { id: '04', name: '04-Sustitución de los CFDI previos.' },
  { id: '05', name: '05-Traslados de mercacías facturados previamente.' },
  { id: '06', name: '06-Facturas generadas por los traslados previos.' },
  { id: '07', name: '07-CFDI por aplicación de anticipo.' }
])
const searchRelationDocuments = ref<string>('')

/* Esquema de validacion campos obligatorios y no obligatorios */
const validationSchema = object().shape({
  payrolls: array()
    .min(1, t('form.valid.minArray'))
    .required(t('form.valid.required')),
  processes: string().required(t('form.valid.required')),
  startDate: date().required(t('form.valid.required')),
  endDate: date().when('startDate', (startDate: any, schema) => {
    if (startDate[0] !== undefined) {
      return schema
        .min(
          startDate,
          `${t('incidents.valid.startDateMin')} ${dayjs(startDate).format(
            'DD/MM/YYYY'
          )}`
        )
        .required(t('form.valid.required'))
    }
    return schema.required(t('form.valid.required'))
  }),
  processedPayrollId: string().required(t('form.valid.required')),
  serialNumber: string()
    .max(25)
    .matches(/^[A-Za-z0-9À-ÿ\u00f1\u00d1\s-_&()]+$/, {
      excludeEmptyString: true,
      message: t('form.valid.alphanumeric')
    }),
  folioNumber: string()
    .max(40)
    .matches(/^[A-Za-z0-9À-ÿ\u00f1\u00d1\s-_&()]+$/, {
      excludeEmptyString: true,
      message: t('form.valid.alphanumeric')
    }),
  regymeType: string().required(t('form.valid.required')),
  paymentFrequency: string().required(t('form.valid.required')),
  paymentDate: date().required(t('form.valid.required')),
  employees: array()
    .min(1, t('form.valid.employeeSelectionRequired'))
    .required(t('form.valid.required'))
})

const validationSchemaStamped = object().shape({
  closedPayrollId: string().required(t('form.valid.required')),
  stampedDate: date().required(t('form.valid.required'))
})

const activeSchema = computed(() => {
  return !isStampedPayroll.value && wantStampedPayroll.value
    ? validationSchema.concat(validationSchemaStamped)
    : validationSchema
})

/* Uso del metodo de validación, retorno de errores y metodo para reiniciar formulario */
const { handleSubmit, errors } = useForm<any /*paramentPayrollStandard*/>({
  initialValues: {
  },
  validationSchema: activeSchema,
  validateOnMount: false
})

/* Definicion campos del formulario */
const { value: payrolls } = useField<string[]>(ref('payrolls'))
const { value: payrollId } = useField<string>(ref('payrollId'))
const { value: processes } = useField<string>(ref('processes'))
const { value: startDate } = useField<string | undefined>('startDate')
const { value: endDate } = useField<string | undefined>('endDate')
const { value: processedPayrollId } = useField<string>(
  ref('processedPayrollId')
)

const { value: closedPayrollId } = useField<string>(ref('closedPayrollId'))

const { value: stampedDate } = useField<string>(ref('stampedDate'))


const { value: serialNumber } = useField<string>(ref('serialNumber'))
const { value: folioNumber } = useField<string>(ref('folioNumber'))
const { value: employees } = useField<any[]>(ref('employees'))

const { value: paymentDate } = useField<string | undefined>('paymentDate')
const { value: regymeType } = useField<string>(ref('regymeType'))
const { value: paymentFrequency } = useField<string>(ref('paymentFrequency'))
const { value: otherDocuments } = useField<any[]>(ref('otherDocuments'))

/* Metodo que valida los campos obligatorios del formulario */
const onSubmit = handleSubmit((values: any) => {
  values.otherDocuments = state.rows
    .filter((r: any) => r.uuid.length > 0 || r.relationId != null)
    .map((_: any) => ({
      id: _.id,
      relationId: _.relationId,
      uuid: _.uuid
    }))

  generatePayload(values)
  generateGoSockets(createGoSocket4.value)
  if (!isStampedPayroll.value && wantStampedPayroll.value) {
    generateStampedPayroll(values)
  }
})

/* Generar payload */
const generatePayload = (values: any) => {
  createGoSocket4.value = {
    code: 'basics/go-socket-4.0',
    parameters: values
  }
}

/* Generar desprendible */
const generateGoSockets = async (values: any) => {
  loading.value = true
  messageLoading.value = t('loading.report')
  try {
    const response = await generateGoSocket4(values)
    reportRequest(response)
  } catch (error: any) {
    showError(error)
  }
}

const generateStampedPayroll = async (values: any) => {
  try {
    const response = await stampedPayrolls(values)
    console.log('response stamped payroll', response)
  } catch (error: any) {
    showError(error)
  }
}

// Buscar nomina procesada ya timbrada
const getStampedPayrollsList = async () => {
  try {
    if (processedPayrollId.value && processedPayrollId.value !== '') {
      loading.value = true
      const itemsStampedPayrolls = await getStampedPayrolls(
        processedPayrollId.value
      )
      if (itemsStampedPayrolls.length > 0 ) {
        closedPayrollId.value = itemsStampedPayrolls[0].closed_payroll_id
        if (itemsStampedPayrolls[0].stamped_date) isStampedPayroll.value = true
        else isStampedPayroll.value = false
        loading.value = false
      } else {
        isStampedPayroll.value = false
        loading.value = false
      }
    }
  console.log('isStampedPayroll', closedPayrollId.value)  
  } catch (error: any) {
    showError(error)
  }
}

/* Listar nominas cerradas */
const getListClosedPayroll = async () => {
  if (startDate.value === '') startDate.value = undefined
  if (endDate.value === '') endDate.value = undefined
  if (
    itemsClosedPayroll.value === undefined ||
    itemsClosedPayroll.value.length === 0
  ) {
    itemsEmployees.value = []
    employees.value = []
    processedPayrollId.value = ''
  }
  try {
    if (
      payrolls.value != undefined &&
      processes.value != undefined &&
      startDate.value != undefined &&
      endDate.value != undefined
    ) {
      loading.value = true
      messageLoading.value = t('reports.loading.payroll')
      itemsClosedPayroll.value = await getClosedPayrolls(
        payrolls.value,
        processes.value,
        startDate.value,
        endDate.value
      )
      if (itemsClosedPayroll.value.length === 0) {
        itemsEmployees.value = []
        employees.value = []
        processedPayrollId.value = ''
        state.rows = []
        notifications.addNotification({
          title: t('reports.notification.noClosedPayrollsFound'),
          text: t('reports.notification.noClosedPayrolls'),
          type: 'warning',
          time: 10000
        })
      }
      loading.value = false
    }
  } catch (error: any) {
    showError(error)
  }
}

/* Listar empleados */
const getListEmployees = async (ids: string[]) => {
  let _employees: string[]
  let _closedPayroll: any = itemsClosedPayroll.value.filter((_: any) =>
    ids.includes(_.id)
  )
  if (_closedPayroll.length === 0) {
    itemsEmployees.value = []
    employees.value = []
    processedPayrollId.value = ''

    notifications.addNotification({
      title: t('reports.notification.noEmployeesFound'),
      text: t('reports.notification.noEmployees'),
      type: 'warning',
      time: 10000
    })
    return
  }
  _employees = _closedPayroll.flatMap((_: any) => _.employees)
  if (_employees.length === 0) {
    notifications.addNotification({
      title: t('reports.notification.noEmployeesFound'),
      text: t('reports.notification.noEmployees'),
      type: 'warning',
      time: 10000
    })
    return
  }
  let _srtEmployees = await convertArrayToString(_employees)
  itemsEmployees.value = await getEmployees('', '', _srtEmployees)
  employees.value = itemsEmployees.value.map((_: any) => _.id)
}

/* Mostrar notificaciones */
const showRetimbrado = () => {
  // Accedemos a los elementos a través de .value
  const iconChevronEl = iconChevron.value
  const collapseTableEl = collapseTable.value
  const showBtnEl = showBtn.value

  const iconChild = iconChevronEl?.children[0] as HTMLElement | undefined

  // Obtener DOM REAL del componente
  const tableRetimbradoSin = tableRetimbrado.value

  if (!tableRetimbradoSin) {
    console.warn('La tabla aún no está montada')
    return
  }

  const tableRetimbradoEl = tableRetimbradoSin.$el

  const tableHeight = tableRetimbradoEl!.clientHeight + 100

  if (iconChild!.classList.contains('open')) {
    iconChild!.classList.remove('open')
    showBtnEl!.classList.remove('show')
    collapseTableEl!.style.height = '0'
  } else {
    iconChild!.classList.add('open')
    showBtnEl!.classList.add('show')
    collapseTableEl!.style.height = `${tableHeight}%`
  }
}

/* Generar reporte */
const reportRequest = (id: string) => {
  urlFile.value = null
  try {
    refresh.value = setInterval(async () => {
      const response = await getReportRequest(id)
      if (response.status === 'Completado') {
        urlFile.value = response.url
        descriptionModal.value = ''
        typeAlertModal.value = 'success'
        titleModal.value = t('reports.notification.reportGenerated')
        showModal.value = true
        loading.value = false
        clearInterval(refresh.value)
      } else if (response.status === 'Error') {
        typeAlertModal.value = 'error'
        titleModal.value = t('reports.notification.reportNoGenerated')
        descriptionModal.value = response.errors
        showModal.value = true
        loading.value = false
        clearInterval(refresh.value)
      }
    }, 5000)
  } catch (error: any) {
    showError(error)
  }
}

/* Volver */
const back = () => {
  void router.push({ name: 'MainReports' })
}

/* Cerrar modal */
const closeModal = () => {
  showModal.value = false
}

/* Mostrar notificación de error */
const showError = (error: any) => {
  loading.value = false
  if (error.response && error.response.data) {
    const code = getErrorCode(error.response.data?.code)
    notifications.addNotification({
      title: t('error.titles.' + code),
      text: t('error.messages.' + code),
      type: 'error',
      time: 10000
    })
  } else {
    notifications.addNotification({
      title: 'Error',
      text: error,
      type: 'error',
      time: 10000
    })
  }
}

const exportExcelRetimbrado = () => {
  try {
    console.log('Exportando Excel...', state.rows)
    exportExcel(columns.value, state.rows)
  } catch (error: any) {
    showError(error)
  }
}

const openFilePicker = () => {
  if (!fileInput.value) return
  fileInput.value.click()
}

const onFileSelected = (e: Event) => {
  loading.value = true
  const target = e.target as HTMLInputElement
  const file = target?.files?.[0]
  if (!file) return

  const reader = new FileReader()

  reader.onload = (evt: ProgressEvent<FileReader>) => {
    const fileReader = evt.target as FileReader
    if (!fileReader?.result) return

    const data = fileReader.result
    const workbook = XLSX.read(data, { type: 'binary' })

    const sheetName = workbook.SheetNames[0]
    const sheet = workbook.Sheets[sheetName]

    const excelData = XLSX.utils.sheet_to_json(sheet)

    const colKeyMap = Object.fromEntries(
      columns.value.map(c => [c.title, c.key])
    )

    const rowsFromExcel = excelData.map((row: any) => {
      const newRow: Record<string, any> = {}
      for (const colTitle in row) {
        const key = colKeyMap[colTitle]
        if (key) newRow[key] = row[colTitle]
      }
      return newRow
    })

    updateRowsWithExcel(rowsFromExcel)
  }

  reader.readAsBinaryString(file)
  if (fileInput.value) fileInput.value.value = ''
  loading.value = false
}

const updateRowsWithExcel = (excelData: any) => {

  state.rows = state.rows.map((row: StructureRowsRetimbrado) => {
    const matched = excelData.find(
      (ex: any) => ex.codigo_name === row.codigo_name
    )
    
    row.uuid = matched?.uuid
      ? String(matched.uuid)
          .split(',')
          .map((u: string) => u.trim())
      : []
    row.relationId = matched?.relationId ?? null

    return row
  })
}

watch(
  () => employees.value,
  () => {

    const employeeValues = itemsEmployees.value.filter((_: any) =>
      employees.value.includes(_.id)
    )

    state.rows = employeeValues.map((_: any) => ({
      id: _.id,
      codigo_name: _.codigo_name,
      relationId: null,
      uuid: []
    }))
  }
)

onMounted(async () => {
  payrollId.value = storePayroll?.payroll?.id as string
  itemsPayrolls.value = await getPayroll(store.user?.id as string)
  payrolls.value = itemsPayrolls.value
    .filter((_: any) => _.id === storePayroll.payroll?.id)
    .map((_: any) => _.id)
  itemsProcesses.value = await getProcessCountry()
})

onUnmounted(() => {
  clearInterval(refresh.value)
})
</script>
<template>
  <div class="flex flex-col h-full scroll w-full px-3">
    <DLoading v-show="loading" :message="messageLoading" />
    <DModal
      :open="showModal"
      :typeAlert="typeAlertModal"
      :title="titleModal"
      :description="descriptionModal"
      @closeModal="closeModal">
      <template v-slot:description v-if="urlFile != null">
        <a
          class="underline tracking-widest text-blue-600"
          :href="urlFile"
          target="_blank"
          v-html="t('reports.notification.viewFile')" />
      </template>
    </DModal>
    <div style="min-height: 100px" class="bg-gray-50 h-28">
      <div class="leading-4 pt-responsive ml-5">
        <p
          class="text-3xl font-semibold text-shadow text-blue-900"
          v-html="t('reports.goSocketRetimbrado')" />
      </div>
    </div>
    <div class="flex-grow w-full scroll overflow-y-auto">
      <div class="flex flex-col gap-4 mb-5">
        <DCard :footer="true">
          <div class="grid grid-cols-4">
            <div class="col-span-4 lg:col-span-3 sm:pb-5 lg:pb-0 px-3">
              <div class="grid grid-cols-6 gap-4">
                <div class="col-span-6 sm:col-span-3 lg:col-span-2">
                  <DMultiselect
                    :label="t('reports.fields.payrolls')"
                    v-model="payrolls"
                    :options="filterOptions(itemsPayrolls, searchPayroll)"
                    :required="true"
                    :error="errors.payrolls"
                    @filter="searchPayroll = $event"
                    @change="getListClosedPayroll()" />
                </div>
                <div class="col-span-6 sm:col-span-3 lg:col-span-2">
                  <DSelectFieldSearch
                    :label="t('reports.fields.processes')"
                    v-model="processes"
                    :options="filterOptions(itemsProcesses, searchProcess)"
                    :required="true"
                    :error="errors.processes"
                    @filter="searchProcess = $event"
                    @change="getListClosedPayroll()" />
                </div>
                <div class="col-span-6 sm:col-span-3 lg:col-span-2">
                  <DTextField
                    type="date"
                    :label="t('reports.fields.startDate')"
                    v-model="startDate"
                    :required="true"
                    :error="errors.startDate"
                    @change="getListClosedPayroll" />
                </div>
                <div class="col-span-6 sm:col-span-3 lg:col-span-2">
                  <DTextField
                    type="date"
                    :label="t('reports.fields.endDate')"
                    v-model="endDate"
                    :required="true"
                    :error="errors.endDate"
                    @change="getListClosedPayroll" />
                </div>
                <div class="col-span-6 sm:col-span-3 lg:col-span-2">
                  <DSelectFieldSearch
                    :label="t('reports.fields.processedPayrolls')"
                    v-model="processedPayrollId"
                    :required="true"
                    :options="
                      filterOptions(itemsClosedPayroll, searchClosedPayroll)
                    "
                    :error="errors.processedPayrollId"
                    @filter="searchClosedPayroll = $event"
                    @change="getListEmployees([processedPayrollId]), getStampedPayrollsList()" />
                </div>
                <hr class="col-span-6" />
                <h1
                  class="col-span-6 text-gray-600"
                  v-html="t('reports.fields.additionals')" />
                <div class="col-span-6 sm:col-span-3 lg:col-span-2">
                  <DTextField
                    type="text"
                    :label="t('reports.fields.serieNumber')"
                    v-model="serialNumber"
                    :error="errors.serialNumber" />
                </div>
                <div class="col-span-6 sm:col-span-3 lg:col-span-2">
                  <DTextField
                    type="text"
                    :label="t('reports.fields.folioNumber')"
                    v-model="folioNumber"
                    :error="errors.folioNumber" />
                </div>
                <div class="col-span-6 sm:col-span-3 lg:col-span-2">
                  <DSelectFieldSearch
                    :label="t('reports.fields.regymenType')"
                    v-model="regymeType"
                    :required="true"
                    :options="filterOptions(itemsRegymeType, searchRegymeType)"
                    :error="errors.regymeType"
                    @filter="searchRegymeType = $event" />
                </div>
                <div class="col-span-6 sm:col-span-3 lg:col-span-2">
                  <DSelectFieldSearch
                    :label="t('reports.fields.paymentFrequency')"
                    v-model="paymentFrequency"
                    :required="true"
                    :options="
                      filterOptions(
                        itemsPaymentFrequency,
                        searchPaymentFrequency
                      )
                    "
                    :error="errors.paymentFrequency"
                    @filter="searchPaymentFrequency = $event" />
                </div>
                <div class="col-span-6 sm:col-span-3 lg:col-span-2">
                  <DTextField
                    type="date"
                    :label="t('reports.fields.paymentDate')"
                    v-model="paymentDate"
                    :required="true"
                    :error="errors.paymentDate" />
                </div>
                <div class="col-span-6 sm:col-span-3 lg:col-span-2 flex justify-start items-center">
                  <DToggleField
                    v-model="wantStampedPayroll"
                    :label="t('reports.fields.wantStampedPayroll')" />
                </div> 
              </div>
              <div 
              v-if="wantStampedPayroll && processedPayrollId"  
              class="grid grid-cols-6 gap-4">
                <br/>
                <hr class="col-span-6" />
                <h1
                  class="col-span-6 text-gray-600"
                  v-html="t('reports.columns.actionAdittional')" />
                <div v-if="isStampedPayroll" class="col-span-6 sm:col-span-3 lg:col-span-2
         flex items-center justify-end gap-2">
                  <DIcon name="CertificateIcon1"  class="w-14 h-14" />
                  <p
                    :class="['text-green-600']"
                    class="text-center tracking-wider text-lg font-medium">
                    {{ t('reports.notification.alreadyStampedPayroll') }}
                  </p>
                </div>
                <div v-else class="col-span-6 sm:col-span-3 lg:col-span-2">
                  <DTextField
                    type="date"
                    :label="t('reports.fields.stampedDate')"
                    v-model="stampedDate"
                    :required="true"
                    :error="errors.stampedDate" />
                </div>
              </div>
            </div>
            <div
              class="flex flex-col col-span-4 lg:col-span-1 border-0 border-t-2 lg:border-l-4 p-2 lg:border-t-0">
              <div class="pl-4 pr-4 lg:pr-0 flex justify-between">
                <h3 class="text-md font-medium leading-6 text-gray-900">
                  {{ t('reports.fields.employees') }}
                </h3>
                <h3
                  class="text-md font-medium leading-6 text-gray-900 flex items-center">
                  <DEmployeesFilter
                    :items="itemsEmployees"
                    v-model="employees" />
                  <FilterLinesIcon class="w-8" />
                </h3>
              </div>
              <div
                class="flex justify-center items-center p-5 sm:gap-4 lg:gap-3 xl:gap-4">
                <Users01Icon class="w-20 h-20" />
                <h1 class="text-5xl font-medium leading-6 text-gray-900">
                  {{
                    `${
                      employees?.length === undefined ? 0 : employees?.length
                    }/${itemsEmployees?.length}`
                  }}
                </h1>
              </div>
              <div class="text-xs text-red-500 self-center">
                {{ errors.employees }}
              </div>
            </div>
          </div>
          <template v-slot:footer>
            <div class="flex justify-end items-start gap-2">
              <DBtn
                type="button"
                color="secondary"
                @click="back"
                class="px-5 py-2 font-semibold"
                v-html="t('form.button.back')" />
              <DBtn
                color="excel"
                class="px-5 py-2 font-semibold"
                @click.prevent="onSubmit()"
                v-html="`${ !isStampedPayroll && wantStampedPayroll ? t('reports.accountingEntry.button.stampedGenereted') : t('form.button.generateFile')}`" />
            </div>
          </template>
        </DCard>
      </div>
      <!-- Retimbrado -->
      <div
        class="flex flex-col justify-between border-2 sm:w-full rounded-lg bg-white h-auto min-h-0">
        <main class="p-5 pb-3 flex flex-col">
          <div class="grid grid-cols-4">
            <div class="col-span-4 md:col-span-2 flex items-center">
              <h3
                class="text-lg font-medium leading-6 text-gray-900 flex"
                :class="{ 'cursor-pointer': state.rows.length != 0 }">
                {{ t('reports.fields.documents') }}
                <span
                  class="ml-2 bg-blue-600 text-white py-1 px-1.5 text-sm rounded-full w-9 h-7 text-center"
                  style="margin-top: -2px">
                  {{ state.rows.length }}
                </span>
                <div
                  id="iconChevron"
                  ref="iconChevron"
                  @click="state.rows.length != 0 ? showRetimbrado() : ''">
                  <ChevronDownIcon
                    v-if="state.rows.length != 0"
                    class="iconChevron ml-2 mt-0.5 w-5" />
                </div>
              </h3>
            </div>
            <div
              v-if="state.rows.length != 0"
              class="col-span-4 md:col-span-2"
              id="showBtn"
              ref="showBtn">
              <div class="flex justify-end items-start gap-2">
                <DBtn
                  :icon="'UploadCloud'"
                  color="secondary"
                  type="button"
                  @click="openFilePicker()">
                  {{ t('reports.button.uploadFile') }}
                </DBtn>
                <input
                  ref="fileInput"
                  class="hidden"
                  type="file"
                  accept=".xlsx,.xls"
                  @change="onFileSelected" />
                <DBtn
                  color="excel"
                  @click.prevent="exportExcelRetimbrado()"
                  v-html="t('form.button.excel')" />
              </div>
            </div>
          </div>
          <div
            v-if="state.rows.length != 0"
            id="collapseTable"
            ref="collapseTable">
            <hr class="mt-2 mb-4 border-1 border-gray-300" />
            <PaginatedTable
              ref="tableRetimbrado"
              id="tableRetimbrado"
              :columns="columns"
              :rows="state.rows"
              :with-search="true"
              :with-pagination="true"
              height-table="100%">
              <template #row="{ row, columns }">
                <td
                  v-for="column in columns"
                  :key="row.id + '-' + column.key"
                  class="px-3">
                  <div
                    v-if="column.key === 'codigo_name'"
                    class="max-w-md overflow-visible whitespace-nowrap text-ellipsis">
                    {{ row[column.key] }}
                  </div>
                  <div
                    v-if="column.key === 'relationId'"
                    class="max-w-md overflow-visible whitespace-nowrap text-ellipsis">
                    <DSelectFieldSearch
                      v-model="row[column.key]"
                      :options="
                        filterOptions(
                          itemsRelationDocuments,
                          searchRelationDocuments
                        )
                      "
                      @filter="searchRelationDocuments = $event" />
                  </div>
                  <div
                    v-if="column.key === 'uuid'"
                    class="flex flex-col col-span-1 md:col-span-2 border-0 border-t-2 md:border-2 p-4 md:border-t-0">
                    <DTagsField
                      v-model="row[column.key]"
                      :colorTags="'secondary'"
                      :placeholder="t('reports.fields.otherDocuments')" />
                  </div>
                </td>
              </template>
            </PaginatedTable>
          </div>
        </main>
      </div>
    </div>
  </div>
</template>
<style>
.iconChevron {
  transform: rotate(0deg);
  -moz-transition: transform 0.3s linear;
  -ms-transition: transform 0.3s linear;
  -o-transition: transform 0.3s linear;
  -webkit-transition: transform 0.3s linear;
  transition: transform 0.3s linear;
}
.iconChevron.open {
  transform: rotate(180deg);
}

#collapseTable {
  height: 0;
  padding-top: 10px;
  overflow: hidden;
  -moz-transition: all 0.6s ease-in-out;
  -ms-transition: all 0.6s ease-in-out;
  -o-transition: all 0.6s ease-in-out;
  -webkit-transition: all 0.6s ease-in-out;
  transition: all 0.6s;
}
#showBtn {
  opacity: 0;
  transition: opacity 0.6s linear;
}
#showBtn.show {
  opacity: 1;
}
</style>
